/**
 * CinemaHero — full-bleed 100svh cinematic hero for public galleries.
 * Turbo 360 "Cinema" design language: dark/photo-forward, 10px white inner
 * frame, signal-wave watermark, Space Mono kicker, red rule, Archivo Expanded
 * UPPERCASE title. Falls back to the red brand gradient when no cover photo.
 *
 * Shared by PublicGallery, SimpleGallery, PortfolioGallery, BrandedGallery.
 */
const CinemaHero = ({
  imageUrl,
  focalPoint,
  name,
  kicker,
  showScroll = true,
  logoSrc = '/turbo-logo-white.png',
  // when there's no cover photo, render the red brand fallback hero
  // (white inner frame + white logo + white watermark + white rule)
  fallback,
}) => {
  const hasImage = !!imageUrl && !fallback;
  // long names wrap gracefully (text-wrap:balance, tighter size, max 18ch)
  const isLong = (name || '').length > 22;

  return (
    <section
      className="relative w-full overflow-hidden bg-tiq-nearblack"
      style={{ height: '100svh', minHeight: 600 }}
    >
      {hasImage ? (
        <>
          <img
            src={imageUrl}
            alt={name || ''}
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              filter: 'brightness(0.82) saturate(1.05)',
              objectPosition: focalPoint ? `${focalPoint.x}% ${focalPoint.y}%` : 'center',
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 24%, rgba(0,0,0,0) 44%, rgba(0,0,0,0.85) 100%)',
            }}
          />
        </>
      ) : (
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg,#BF212F,#8B1923)' }} />
      )}

      {/* 10px white inner frame */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ border: 'clamp(6px,1vw,10px) solid #fff' }}
      />

      {/* signal-wave watermark, top-right */}
      <img
        src="/turbo-mark-white.png"
        alt=""
        className="absolute pointer-events-none"
        style={{
          top: 'clamp(-46px,-4.5vw,-26px)',
          right: 'clamp(-46px,-4.5vw,-26px)',
          width: 'min(40vw,480px)',
          opacity: 0.2,
          zIndex: 2,
          transform: 'rotate(-8deg)',
        }}
      />

      {/* content, bottom-left */}
      <div
        className="absolute z-[3]"
        style={{
          left: 'clamp(24px,4vw,60px)',
          right: 'clamp(24px,4vw,60px)',
          bottom: 'clamp(48px,8vh,76px)',
        }}
      >
        <img
          src={logoSrc}
          alt="Turbo 360"
          className="block w-auto"
          style={{ height: 'clamp(22px,2.6vw,28px)', marginBottom: 'clamp(16px,2.6vh,24px)' }}
        />
        {kicker && (
          <div
            className="tiq-kicker"
            style={{ fontSize: 'clamp(11px,1.4vw,14px)', color: 'rgba(255,255,255,0.85)' }}
          >
            {kicker}
          </div>
        )}
        <div
          style={{
            width: 54,
            height: 4,
            background: '#fff',
            margin: 'clamp(14px,2vh,20px) 0 clamp(12px,2vh,18px)',
            backgroundColor: hasImage ? '#BF212F' : '#fff',
          }}
        />
        <h1
          className="font-tiq-display text-white uppercase m-0"
          style={{
            fontWeight: 900,
            fontSize: isLong ? 'clamp(34px,5.4vw,52px)' : 'clamp(42px,8.4vw,92px)',
            lineHeight: 0.92,
            letterSpacing: '-0.025em',
            maxWidth: isLong ? '18ch' : '14ch',
            textWrap: 'balance',
          }}
        >
          {name}
        </h1>
      </div>

      {showScroll && (
        <div className="absolute bottom-[22px] left-1/2 -translate-x-1/2 z-[3] flex flex-col items-center gap-1.5">
          <span className="tiq-kicker" style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>
            SCROLL
          </span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" className="animate-bounce">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      )}
    </section>
  );
};

export default CinemaHero;
